// JanetReturnBridge — the "← Back to X" affordance for Janet's persistent
// object path (Intentional Propulsion Slice 6, issue #169). Mounted once in
// the app shell (desktop Layout + MobileLayout) so it works from ANY
// canonical page Janet routed Aaron through — a Deal, an Expression, a
// Person — without every page needing its own copy. Renders nothing when
// there is no return target, or when Aaron is already on the return
// target's own page (arriveing there directly closes the loop the same way
// as clicking the pill).
//
// This is read-only navigation, never a write — no confirmation gate, one
// click, matching the zoom-out affordance's own directness (ZoomSpine).

import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeft, X } from 'lucide-react'
import { useJanetObject } from '../../contexts/JanetObjectContext'
import { zoomNavigate } from '../../lib/zoomNav'

export default function JanetReturnBridge() {
  const { returnTarget, returnObject, clearObjectPath } = useJanetObject()
  const navigate = useNavigate()
  const location = useLocation()

  if (!returnTarget) return null
  // Already there (arrived by any route) — nothing to bridge back to.
  if (location.pathname === returnTarget.path) return null

  const handleReturn = () => {
    returnObject()
    zoomNavigate(navigate, returnTarget.path)
  }

  return (
    <div
      className="flex items-center gap-2 border-b flex-shrink-0 overflow-x-auto scrollbar-none"
      style={{ background: 'var(--ns-paper, #fff)', borderColor: 'var(--ns-hair-soft, #e5e0d8)', padding: '5px 14px' }}
      data-testid="janet-return-bridge"
    >
      <button
        onClick={handleReturn}
        className="flex items-center gap-1.5 hover:opacity-70 shrink-0"
        style={{ fontFamily: 'var(--ns-font-sans)', fontSize: 11.5, fontWeight: 600, color: 'var(--ns-attention, #b45309)' }}
        title={`Back to ${returnTarget.label}`}
      >
        <ArrowLeft size={12} />
        <span className="truncate max-w-[260px]">Back to {returnTarget.label}</span>
      </button>
      <button
        onClick={clearObjectPath}
        className="ml-auto shrink-0 hover:opacity-70 p-0.5"
        style={{ color: 'var(--ns-faint, #a39a8c)' }}
        title="Dismiss"
      >
        <X size={12} />
      </button>
    </div>
  )
}
